import {UseCognitoType} from 'auth/modules/useCognito'
import {USER_TIERS} from '../../offer/business/privateDeals'
import {AuthContextType} from './Auth'

export interface CustomUserData {
  tier?: USER_TIERS[]
  loginCount: number
  auth0_id?: string
}

export interface UserAttributes {
  given_name?: string
  family_name?: string
  phone_number?: string
  phone_number_verified?: boolean
  picture?: string
  email?: string
  email_verified?: boolean
  sub?: string
  /**
   * custom:user_data is a JSON stringified object containing the user data that can be modified by the user itself.
   */
  'custom:user_data'?: string
  /**
   * custom:app_data is a stringified JSON object that is only readable by front-end (no write access). user `tier` is stored in this field.
   * Example: '{"tier":["plus"]}'
   */
  'custom:app_data'?: string
  identities?: string
}

export interface User {
  attributes: UserAttributes
  id?: string
  username: string
  createdAt: string
  updatedAt: string
}

export interface CognitoSession {
  username: string
  pool: Pool
  Session: string
  client: Client
  signInUserSession: SignInUserSession
  authenticationFlowType: string
  keyPrefix: string
  userDataKey: string
}

export interface Client {
  endpoint: string
  fetchOptions: Record<string, any>
}

export interface Pool {
  userPoolId: string
  clientId: string
  client: Client
  advancedSecurityDataCollectionFlag: boolean
}

export interface SignInUserSession {
  idToken: IDToken
  refreshToken: RefreshToken
  accessToken: AccessToken
  clockDrift: number
}

export interface AccessToken {
  jwtToken: string
  payload: AccessTokenPayload
}

export interface AccessTokenPayload {
  sub: string
  iss: string
  client_id: string
  origin_jti: string
  event_id: string
  token_use: string
  scope: string
  auth_time: number
  exp: number
  iat: number
  jti: string
  username: string
}

export interface IDToken {
  jwtToken: string
  payload: IDTokenPayload
}

export interface IDTokenPayload {
  sub: string
  iss: string
  phone_number_verified: boolean
  'cognito:username': string
  origin_jti: string
  aud: string
  event_id: string
  token_use: string
  auth_time: number
  phone_number: string
  exp: number
  iat: number
  jti: string
}

export interface RefreshToken {
  token: string
}

export enum CognitoErrorCodes {
  UsernameExistsException = 'UsernameExistsException',
  UserLambdaValidationException = 'UserLambdaValidationException'
}

export type CognitoError = {
  name: string
  code: CognitoErrorCodes
  message?: string
}

/**
 * Type to be used on useToggleAuth hooks.
 * Pass all the keys within the type.
 *
 * Example of use:
 *
 * const useToggleAuth = (): UseToggleAuth<'isPrivileged' | 'isAuthenticated'> => {
 *   const auth0Values = ...
 *   const isPrivileged = ...
 *   const isAuthenticated = ...
 *   return toggle('...', auth0Values, {
 *     isPrivileged, isAuthenticated
 *   })
 * }
 */
export type UseToggleAuth<keys extends keyof UseCognitoType> =
  | Partial<AuthContextType>
  | Pick<UseCognitoType, keys>
